import { usePageRuntime, usePageRouter } from '@Pages/pageRuntime/pageRuntimeContextValue';
import {
  extractApiErrorBody,
  isValidationError,
  toApiError
} from '@Pages/pageRuntime/sdk/apiErrors';
import type { ApiError, SdkTwoFactorFlow, SdkTwoFactorPending } from '@Pages/pageRuntime/sdk/types';
import {
  type QueryClient,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query';
import axios from 'axios';

export type ChallengeProps = Diagonal.Auth.PageProps.ShowTwoFactorChallengePageProps;
export type SetupProps = Diagonal.Auth.PageProps.TwoFactorEnrollmentPageProps;
export type BackupCodesProps = Diagonal.Auth.PageProps.TwoFactorRecoveryCodesPageProps;
type AnyFlowProps = ChallengeProps | SetupProps | BackupCodesProps;

export type TwoFactorCompletion = Diagonal.Auth.Data.TwoFactorCompletionData;

export type SmsConsentFields = {
  sms_consent: true;
  notice_version: string;
  consent_token: string;
};

/** The error key under which the server ends a pending sign-in that must restart. */
const RESTART_ERROR_KEY = 'two_factor';

export function twoFactorFlowUrl(flow: SdkTwoFactorFlow, resourceId: string): string {
  const base = `/auth/resource/${encodeURIComponent(resourceId)}/two-factor`;

  if (flow === 'setup') {
    return `${base}/setup`;
  }

  return flow === 'backupCodes' ? `${base}/recovery-codes` : base;
}

/**
 * Kept apart from the page API cache head, so a record mutation that refreshes page
 * queries never refetches a flow and repeats its server-side step.
 */
export function twoFactorFlowQueryKey(flow: SdkTwoFactorFlow, resourceId: string | null) {
  return ['two-factor', flow, resourceId] as const;
}

/** Shared completion state covers the interval before the SDK identity reloads. */
export function useTwoFactorCompletion(resourceId: string | null) {
  // Only `setQueryData` fills this entry, so it never fetches.
  return useQuery<TwoFactorCompletion | null>({
    queryKey: ['two-factor', 'completion', resourceId],
    queryFn: skipToken,
    initialData: null,
    staleTime: Infinity
  }).data;
}

/** Backup codes appear once, so the cache never holds them. */
function withoutCodes(props: AnyFlowProps): AnyFlowProps {
  return 'codes' in props && props.codes ? { ...props, codes: null } : props;
}

export function storeTwoFactorFlow(
  queryClient: QueryClient,
  flow: SdkTwoFactorFlow,
  resourceId: string,
  props: AnyFlowProps
): void {
  queryClient.setQueryData(twoFactorFlowQueryKey(flow, resourceId), withoutCodes(props));
}

export function postTwoFactor<T>(url: string, data: Record<string, unknown>): Promise<T> {
  return axios.post<T>(url, data, { headers: { Accept: 'application/json' } }).then(
    (response) => response.data,
    (error: unknown) => {
      throw toApiError(error);
    }
  );
}

export function isRestartError(error: ApiError | null): boolean {
  if (error === null || !isValidationError(error)) {
    return false;
  }

  return extractApiErrorBody(error)?.errors?.[RESTART_ERROR_KEY] !== undefined;
}

/** Leaves the page for a server-resolved destination, so the new session loads in full. */
export function navigateAfterSignIn(url: string): void {
  window.location.assign(url);
}

export type TwoFactorTarget = {
  resourceId: string | null;
  pending: SdkTwoFactorPending | null;
  signedInResourceId: string | null;
  twoFactorEnabled: boolean;
};

/**
 * The resource a two-factor flow belongs to: the given one, else the pending sign-in's,
 * else the signed-in identity's, else the workspace's sole authenticatable resource.
 */
export function useTwoFactorTarget(resourceId?: string): TwoFactorTarget {
  const sdk = usePageRuntime()?.Sdk;
  const pending = sdk?.auth.twoFactor ?? null;
  const signedInResourceId = sdk?.identity?.resource_id ?? null;
  const target =
    resourceId ?? pending?.resourceId ?? signedInResourceId ?? sdk?.auth.resourceId ?? null;
  const resource = sdk?.auth.resources.find((entry) => entry.id === target);

  return {
    resourceId: target,
    pending,
    signedInResourceId,
    twoFactorEnabled: resource?.twoFactorEnabled ?? false
  };
}

function useFlowQuery<TProps extends AnyFlowProps>(
  flow: SdkTwoFactorFlow,
  resourceId: string | null,
  enabled: boolean
) {
  return useQuery<TProps, ApiError>({
    queryKey: twoFactorFlowQueryKey(flow, resourceId),
    enabled: enabled && resourceId !== null,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: () =>
      axios
        .get<TProps>(twoFactorFlowUrl(flow, resourceId ?? ''), {
          headers: { Accept: 'application/json' }
        })
        .then(
          (response) => response.data,
          (error: unknown) => {
            throw toApiError(error);
          }
        )
  });
}

export function useChallengeQuery(resourceId: string | null, enabled: boolean) {
  return useFlowQuery<ChallengeProps>('challenge', resourceId, enabled);
}

// A GET never generates backup codes, so the loaded state holds none.
export function useSetupQuery(resourceId: string | null, enabled: boolean) {
  return useFlowQuery<SetupProps>('setup', resourceId, enabled);
}

export function useBackupCodesQuery(resourceId: string | null, enabled: boolean) {
  return useFlowQuery<BackupCodesProps>('backupCodes', resourceId, enabled);
}

/**
 * Ends the pending sign-in and reloads the auth catalog, so the page returns to its
 * email step. A pending sign-in that already ended cancels the same way.
 */
export function useCancelPendingSignIn(resourceId: string | null) {
  const router = usePageRouter();
  const queryClient = useQueryClient();

  return useMutation<unknown, ApiError, void>({
    mutationFn: () => {
      if (resourceId === null) {
        throw new Error('A two-factor cancel needs a resource.');
      }

      return postTwoFactor(`${twoFactorFlowUrl('challenge', resourceId)}/cancel`, {});
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['two-factor'] });
      router.reload({ only: ['sdkAuth'] });
    }
  });
}

type SendState = {
  /** The number that a send texts, masked. */
  maskedPhone: string | null;
  notice: Diagonal.Auth.Data.SmsConsentNoticeData | null;
  consentRequired: boolean;
  /** Seconds until the server accepts the next send. Only the challenge reports it. */
  resendAvailableIn: number;
  /**
   * Changes with each server answer, so a countdown restarts from it. It is the cache
   * update time, because structural sharing keeps the props object of an equal answer.
   */
  response: number;
};

function sendState(props: AnyFlowProps | undefined, updatedAt: number): SendState | null {
  if (props === undefined) {
    return null;
  }

  return {
    maskedPhone: props.maskedPhone,
    notice: props.smsNotice,
    consentRequired: props.smsConsentRequired,
    resendAvailableIn: 'resendAvailableIn' in props ? props.resendAvailableIn : 0,
    response: updatedAt
  };
}

/**
 * Sends a code for one flow, with consent when the number has none yet. Only the
 * locked send components call it, so every send shows the SMS terms beside it.
 */
export function useTwoFactorSend(flow: SdkTwoFactorFlow, resourceId: string | null) {
  const queryClient = useQueryClient();
  const query = useFlowQuery<AnyFlowProps>(flow, resourceId, true);
  const state = sendState(query.data, query.dataUpdatedAt);

  const mutation = useMutation<AnyFlowProps, ApiError, SmsConsentFields | null>({
    mutationFn: (consent) => {
      if (resourceId === null) {
        throw new Error('A two-factor send needs a resource.');
      }

      const url = twoFactorFlowUrl(flow, resourceId);
      const fields = consent ?? {};

      if (flow === 'challenge') {
        return postTwoFactor(consent ? `${url}/consent` : `${url}/resend`, fields);
      }

      if (flow === 'backupCodes') {
        return postTwoFactor(`${url}/send-code`, fields);
      }

      const setup = queryClient.getQueryData<SetupProps>(
        twoFactorFlowQueryKey('setup', resourceId)
      );

      return postTwoFactor(url, {
        operation: setup?.step === 'authorize' ? 'send-current' : 'send',
        ...fields
      });
    },
    onSuccess: (props) => {
      if (resourceId !== null) {
        storeTwoFactorFlow(queryClient, flow, resourceId, props);
      }
    }
  });

  return {
    state,
    send: (consent: SmsConsentFields | null) =>
      mutation.mutateAsync(consent).then(
        () => true,
        () => false
      ),
    isPending: mutation.isPending,
    error: mutation.error
  };
}
